import { createSelector } from 'reselect';
import { get } from 'lodash';

import { currentAppointmentHavePermissions } from 'modules/appointmentInfo/selectors/currentAppointment';
import mediaStatuses from 'modules/appointmentInfo/constants/mediaStatus';
import { CLOSED_APPOINTMENT_STATUSES } from 'modules/common/constants/appointmentStatuses';
import { getCurrentAppointment } from './currentAppointment';

const getFileMetaDataList = state => state.appointmentInfo.patientMedia.fileMetaDataList;
const getLocalFilesList = state => state.appointmentInfo.patientMedia.files;
const getCurrentAppointmentAttachments = state => state.appointmentInfo.currentAppointment.attachments;


export const isCurrentAppointmentArchived = createSelector(
  getCurrentAppointment,
  ({ status }) => CLOSED_APPOINTMENT_STATUSES.includes(status),
);

const serviceHasGallery = createSelector(
  getCurrentAppointment,
  (currentAppointment) => get(currentAppointment, 'service.config.fileUploadsEnabled', false),
);

const getThumbnail = thumbnail => {
  return thumbnail && !/^data:image/.test(thumbnail)
    ? `data:image/jpeg;base64,${ thumbnail }`
    : thumbnail;
};

export const getAllowedActions = createSelector(
  isCurrentAppointmentArchived,
  currentAppointmentHavePermissions({ APPOINTMENTS_FILES: ['delete'] }, 'service.config.fileUploadsEnabled'),
  (isArchived, canBeRemoved) => {
    const allowedActions = [
      'ZOOM_IN',
      'ZOOM_OUT',
      'PREV',
      'NEXT',
      'ROTATE_LEFT',
      'ROTATE_RIGHT',
      'RESET',
      'TOGGLE_EXPAND',
      'CLOSE',
    ];

    if (!isArchived && canBeRemoved) {
      return ['DELETE', ...allowedActions];
    }

    return allowedActions;
  },
);

export const getViews = createSelector(
  getFileMetaDataList,
  isCurrentAppointmentArchived,
  currentAppointmentHavePermissions({ APPOINTMENTS_FILES: ['update'] }, 'service.config.fileUploadsEnabled'),
  currentAppointmentHavePermissions({ APPOINTMENTS_FILES: ['delete'] }, 'service.config.fileUploadsEnabled'),
  (files, isArchived, canBeUpdated, canBeRemoved) => {
    return files
      .map(({
        metaDataId,
        name,
        status,
        comment,
        mimeType,
        downloadRoute,
        thumbnail,
      }) => ({
        id: metaDataId,
        name,
        status,
        comment,
        mimeType,
        downloadRoute,
        thumbnail: getThumbnail(thumbnail),
        canBeUpdated: mediaStatuses.UPLOADED === status && !isArchived && canBeUpdated,
        canBeRemoved: [mediaStatuses.REJECTED, mediaStatuses.UPLOADED].includes(status) && !isArchived && canBeRemoved,
      }));
  },
);

export const getUploads = createSelector(
  getLocalFilesList,
  files => [...files].reverse(),
);

export const getFilesTotal = createSelector(
  getLocalFilesList,
  getFileMetaDataList,
  (localFiles, remoteFiles) => localFiles.length + remoteFiles.length,
);

export const isFABEnabled = createSelector(
  state => state.appointmentInfo.infoTab.tabId,
  currentAppointmentHavePermissions({ APPOINTMENTS_FILES: ['create'] }, 'service.config.fileUploadsEnabled'),
  isCurrentAppointmentArchived,
  (tabId, canUploadFiles, isArchived) =>
    !isArchived
    && ['1', '3'].includes(tabId)
    && canUploadFiles,
);

export const isGalleryEnabled = createSelector(
  serviceHasGallery,
  currentAppointmentHavePermissions({ APPOINTMENTS_FILES: ['read'] }),
  getCurrentAppointmentAttachments,
  (isServiceHasGallery, canReadGallery, attachments) => canReadGallery && (isServiceHasGallery || attachments.length > 0),
);

